import { defineStore } from 'pinia'
import { ref, nextTick } from 'vue'

export type ModalPresentation = 'default' | 'fullscreen'

export const useStoreModals = defineStore('storeModals', () => {
  const isModalOpen = ref(false)
  const modalProps = ref({})
  const modalType = ref('')
  const modalCanDismiss = ref(true)
  const modalPresentation = ref<ModalPresentation>('default')

  const childModals = ref([])

  function openModal(params: {
    type: string
    props?: any
    canDismiss?: boolean
    presentation?: ModalPresentation
  }) {
    modalProps.value = params.props || {}
    modalType.value = params.type
    modalCanDismiss.value = params.canDismiss ?? true
    modalPresentation.value = params.presentation ?? 'default'

    // If a modal is already open, close it and open the new one
    // But do it asynchronously to avoid race conditions
    if(isModalOpen.value) {
      isModalOpen.value = false
      nextTick(() => {
        isModalOpen.value = true
      })
    } else {
      isModalOpen.value = true
    }
  }

  function closeModal() {
    isModalOpen.value = false
  }

  function openChildModal(params: {
    type: string
    props?: any
    canDismiss?: boolean
  }) {
    // Create an object to store the modal data and functions
    // .isOpen (boolean) - whether the modal is open or not
    // .type (string) - the type of modal
    // .props (object) - the props to pass to the modal
    // .canDismiss (boolean) - whether the modal can be dismissed or not
    // .handleClose (function) - a function to close the modal
    // .handleOpen (function) - a function to open the modal
    // .parentModal (object) - the parent modal object

    const childModal = {
      isOpen: ref(true),
      type: params.type,
      props: params.props || {},
      canDismiss: params.canDismiss ?? true,
      handleClose: () => {
        childModal.isOpen.value = false
      },
      handleOpen: () => {
        childModal.isOpen.value = true
      },
      parentModal: null
    }

    childModals.value.push(childModal)
  }

  return {
    childModals,
    isModalOpen,
    modalProps,
    modalType,
    modalCanDismiss,
    modalPresentation,
    openModal,
    closeModal,
    openChildModal
  }
})
